import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Drift watcher — passive tick that emits tiered surfaces per PRD §9.5:
 *
 *   drift ≤ 15m   → inline +Xm chip only (rendered by ScheduleGrid already).
 *   16–45m        → single toast strip, max 1 per source block per 30 minutes.
 *   > 45m         → returned as a `banner` for the Schedule page to render.
 *   locked knock-on drift → escalated to banner regardless of magnitude.
 *
 * Operates entirely on the already-computed `blocksWithDrift` array (live drift
 * is computed in Schedule.jsx via /lib/drift.js). The hook keeps a per-block
 * "last surfaced at" timestamp to honour the rate-limit and to avoid spamming
 * toasts on every 30s tick.
 */
const TOAST_FLOOR_MIN = 16;
const TOAST_CEILING_MIN = 45;
const BANNER_FLOOR_MIN = 46;
const REPEAT_AFTER_MS = 30 * 60 * 1000; // 30 min

export function useDriftWatcher({ blocks, tasksById, enabled = true }) {
  const lastSurfacedRef = useRef(new Map()); // block_id → epoch ms
  const dismissedRef = useRef(new Set()); // block_ids the user dismissed (resets on next tier escalation)
  const [banners, setBanners] = useState([]); // [{block_id, task_title, drift_min, kind, dismissed}]

  useEffect(() => {
    if (!enabled) return;
    const now = Date.now();
    const nextBanners = [];
    for (const b of blocks) {
      const drift = Math.abs(b.drift_min || 0);
      if (drift < TOAST_FLOOR_MIN) continue;
      const task = tasksById[b.task_id];
      const title = task?.title || "(untitled)";
      const lastAt = lastSurfacedRef.current.get(b.id) || 0;
      const elapsed = now - lastAt;

      if (drift >= BANNER_FLOOR_MIN || b.is_locked) {
        // Banner tier: persists until dismissed.
        nextBanners.push({
          block_id: b.id,
          task_title: title,
          drift_min: drift,
          kind: b.is_locked ? "locked-knock-on" : "large-drift",
        });
        // Reset dismissal once drift continues to grow.
        if (dismissedRef.current.has(b.id) && drift > (lastSurfacedRef.current.get(`${b.id}-drift`) || 0) + 15) {
          dismissedRef.current.delete(b.id);
        }
      } else if (drift >= TOAST_FLOOR_MIN && drift <= TOAST_CEILING_MIN && elapsed > REPEAT_AFTER_MS) {
        // Toast tier — rate-limited.
        toast.warning(`${title} drifting ${drift > 0 ? "+" : ""}${drift}m`, {
          duration: 5000,
          id: `drift-${b.id}`,
        });
        lastSurfacedRef.current.set(b.id, now);
      }
      lastSurfacedRef.current.set(`${b.id}-drift`, drift);
    }
    // Keep dismissals out of the banner list.
    setBanners(nextBanners.filter((bn) => !dismissedRef.current.has(bn.block_id)));
  }, [blocks, tasksById, enabled]);

  const dismissBanner = (blockId) => {
    dismissedRef.current.add(blockId);
    setBanners((prev) => prev.filter((b) => b.block_id !== blockId));
  };

  return { banners, dismissBanner };
}
